import { useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { withErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { ColorBrandBlue5 } from 'autogen/design-tokens/tokens';
import type { KlarnaNudgeViewModel } from 'autogen/swagger/all';
import useSegmentation from 'hooks/user/useSegmentation';

// #region Styles
const KlarnaContainer = styled.div`
    min-height: 56px; // placeholder height for Klarna widget to load
`;

const StyledKlarnaPlacement = styled('klarna-placement')`
    &::part(osm-container) {
        border: 0;
        padding: 0;
    }
`;
// #endregion Styles

function KlarnaNudgeWidget(props: KlarnaNudgeViewModel) {
    const { amount, apiKey, clientIdentifier, scriptUrl, ...restProps } = props;
    const { culture } = useSegmentation();
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [scriptError, setScriptError] = useState(false);

    useEffect(() => {
        if (!scriptUrl) return;
        const script = document.createElement('script');
        script.async = true;
        script.src = scriptUrl;
        script.setAttribute('data-client-id', clientIdentifier);
        script.onload = () => setScriptLoaded(true);
        script.onerror = () => setScriptError(true);
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, [scriptUrl, clientIdentifier]);

    if (scriptError) {
        // Throwing error to trigger error boundary
        throw new Error('Failed to load Klarna script');
    }

    return (
        <>
            <KlarnaContainer {...restProps}>
                {!scriptLoaded ? (
                    <ContentLoader
                        speed={2}
                        preserveAspectRatio="none"
                        viewBox="0 0 400 40"
                        backgroundColor="#f3f3f3"
                        foregroundColor={ColorBrandBlue5}
                    >
                        <rect x="0" y="5" rx="8" ry="8" width="71" height="30" />
                        <rect x="84" y="8" rx="2" ry="2" width="352" height="8" />
                        <rect x="84" y="23" rx="2" ry="2" width="70" height="8" />
                    </ContentLoader>
                ) : (
                    <StyledKlarnaPlacement
                        data-key="credit-promotion-badge"
                        data-locale={culture}
                        data-purchase-amount={Math.trunc(amount * 100)}
                    />
                )}
            </KlarnaContainer>
        </>
    );
}

export default withErrorBoundary(KlarnaNudgeWidget, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
